import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { AtLeast } from 'common/types/Atleast';
import { Image } from 'common/components/image/Image';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import BudgetLineImagesService from 'api/budgetLineImages/BudgetLineImagesService';
import CustomFile from 'common/models/CustomFile';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import { Header, Text } from 'common/components/texts/Texts';
import Button from 'common/components/button/Button';
import styles from './LineImagePicker.module.scss';

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
    isElementLine: boolean;
    onChange: (patch: AtLeast<BudgetLineDto, 'key'>) => void;
    className?: string;
}

const ACCEPTED_MIME_TYPES = 'image/png,image/jpeg,image/jpg';

const LineImagePicker = ({ line, disabled, isElementLine, onChange, className }: Props) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [pendingMirrorDecision, setPendingMirrorDecision] = useState<{ attachmentId: string; url: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasImage = !!line.imageUrl;

    const openFilePicker = () => {
        setMenuOpen(false);
        fileInputRef.current?.click();
    };

    const finalizeUpload = (attachmentId: string, url: string, mirrorToElement: boolean) => {
        onChange({
            key: line.key,
            imageUrl: url,
            pendingImageAttachmentId: attachmentId,
            clearImageOverride: false,
            mirrorImageToElement: mirrorToElement,
        });
    };

    const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const customFile: CustomFile = file as CustomFile;
            try {
                setIsUploading(true);
                const result = await BudgetLineImagesService.uploadPending(customFile);
                if (isElementLine) {
                    setPendingMirrorDecision({ attachmentId: result.attachmentId, url: result.url });
                } else {
                    finalizeUpload(result.attachmentId, result.url, false);
                }
            } catch (err) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t upload budget line image', err);
                Toast.error(t('messages.error_load_info'));
            } finally {
                setIsUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const onRemove = () => {
        setMenuOpen(false);
        onChange({
            key: line.key,
            imageUrl: null,
            imageAttachmentId: null,
            pendingImageAttachmentId: null,
            clearImageOverride: true,
            mirrorImageToElement: false,
        });
    };

    const onMirrorChoice = (mirror: boolean) => {
        if (pendingMirrorDecision) {
            finalizeUpload(pendingMirrorDecision.attachmentId, pendingMirrorDecision.url, mirror);
        }
        setPendingMirrorDecision(null);
    };

    return (
        <div className={`${styles.container} ${className ?? ''}`}>
            <div
                className={`${styles.imageWrapper} ${disabled ? styles.disabled : ''}`}
                onClick={() => { if (!disabled && !isUploading) setMenuOpen(v => !v); }}
            >
                <Image src={line.imageUrl ?? null} containerClassName={styles.image} loading='lazy' />
                {!disabled && <div className={styles.overlay}>
                    <span className={styles.overlayText}>{t('budgets.budget.lines.image.configure')}</span>
                </div>}
            </div>

            {menuOpen && (
                <>
                    <div className={styles.dropdownOverlay} onClick={() => setMenuOpen(false)} />
                    <div className={styles.dropdown}>
                        {!hasImage && (
                            <button type='button' className={styles.dropdownItem} onClick={openFilePicker}>
                                {t('budgets.budget.lines.image.add')}
                            </button>
                        )}
                        {hasImage && (
                            <button type='button' className={styles.dropdownItem} onClick={openFilePicker}>
                                {t('budgets.budget.lines.image.change')}
                            </button>
                        )}
                        {hasImage && (
                            <button type='button' className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} onClick={onRemove}>
                                {t('budgets.budget.lines.image.remove')}
                            </button>
                        )}
                    </div>
                </>
            )}

            <input
                ref={fileInputRef}
                type='file'
                accept={ACCEPTED_MIME_TYPES}
                className={styles.hiddenInput}
                onChange={onFileSelected}
            />

            <Modal isOpen={pendingMirrorDecision !== null} style={{ content: { minWidth: 400 } }} onRequestClose={() => onMirrorChoice(false)}>
                <div>
                    <ModalHeader>
                        <Header>{t('budgets.budget.lines.image.mirror_title').toUpperCase()}</Header>
                    </ModalHeader>
                    <ModalBody>
                        <Text>{t('budgets.budget.lines.image.mirror_message')}</Text>
                        <Text className={styles.mirrorWarning}>{t('budgets.budget.lines.image.mirror_save_warning')}</Text>
                    </ModalBody>
                    <ModalFooter className='text-right'>
                        <Button fw variant='secondary' onClick={() => onMirrorChoice(false)}>{t('budgets.budget.lines.image.mirror_no')}</Button>
                        <Button onClick={() => onMirrorChoice(true)}>{t('budgets.budget.lines.image.mirror_yes')}</Button>
                    </ModalFooter>
                </div>
            </Modal>
        </div>
    );
};

export default LineImagePicker;
